
import { getWASM, initWASM } from './service/get-wasm.js';
import { getPixelSize } from './utils/get-pixel-size.js';


export class ColorAlchemy {
    /**
     * @param { number } instance
     * @param { number } inMode
     * @param { number } outMode
     */
    constructor(instance, inMode, outMode) {
        this._instance = instance;
        this._inMode = inMode;
        this._outMode = outMode;
    }

    /**
     * 转换颜色色彩空间
     * @param { Uint8Array } colorBuffer - 输入颜色数据
     * @returns { Uint8Array }
     */
    transform(colorBuffer) {
        const wasm = getWASM();
        const inputPtr = wasm._malloc(colorBuffer.length);

        wasm.HEAPU8.set(colorBuffer, inputPtr);
        const outputPtr = wasm._transform(this._instance, inputPtr, colorBuffer.length);
        const size = Math.floor(colorBuffer.length / getPixelSize(this._inMode)) * getPixelSize(this._outMode);
        const outputBuffer = wasm.HEAPU8.subarray(outputPtr, outputPtr + size).slice(0);
        wasm._free(inputPtr)
        wasm._free(outputPtr)
        return outputBuffer;
    }

    /**
     * 校验传入的颜色是否在色彩空间中
     * @param { Uint8Array } colorBuffer - 输入颜色数据
     * @param { number } [threshold] - 色域检查阀值， 默认为 1, 最小为 1
     * @returns { Uint8Array } 判断所有像素点是否在色域中的数组，255 为超出色域,
     */
    validGamut(colorBuffer, threshold) {
        const wasm = getWASM();
        const inputPtr = wasm._malloc(colorBuffer.length);

        threshold = threshold || 1;

        wasm.HEAPU8.set(colorBuffer, inputPtr);
        const outputPtr = wasm._inGamut(this._instance, inputPtr, colorBuffer.length, threshold);
        const size = Math.floor(colorBuffer.length / getPixelSize(this._inMode));
        const outputBuffer = wasm.HEAPU8.subarray(outputPtr, outputPtr + size).slice(0);
        wasm._free(inputPtr)
        wasm._free(outputPtr)

        return outputBuffer;
    }

    /**
     * 判断传入颜色是否在全在色彩空间中
     * @param { Uint8Array } colorBuffer - 输入颜色数据
     * @param { Number } proportion - 检测比例，色域内的像素点 / 总像素点 >= 比例时，认为传入的颜色值在色彩空间中
     * @param { number } [threshold] - 色域检查阀值， 默认为 1, 最小为 1
     * @returns { Boolean }
     */
    inGamut(colorBuffer, proportion, threshold) {
        proportion = proportion || 1;
        const outputBuffer = this.validGamut(colorBuffer, threshold);
        
        const maxCount = Math.floor(Math.max(1, (1 - proportion) * outputBuffer.length));
        let count = 0;
        for(let i = 0; i < outputBuffer.length; i++) {
            if (outputBuffer[i] === 255) {
                count += 1;
                if (count >= maxCount) {
                    return false;
                }
            }
        }
        return true;
    }

    /**
     * 销毁 color alchemy 实例
     */
    destroy() {
        const wasm = getWASM();
        wasm._destroy(this._instance);
    }
}

/**
 * 创建 color alchemy 实例
 * @param { Uint8Array } inputProfileBuffer - 输入色彩空间的 profile
 * @param { number } inMode - 输入格式
 * @param { Uint8Array } outputProfileBuffer - 输出色彩空间的 profile
 * @param { number } outMode - 输出格式
 * @param { number } intent - 映射方式
 * @param { number } flag - 标识
 * @returns { Promise<ColorAlchemy> }
 */
export function createColorAlchemy(inputProfileBuffer, inMode, outputProfileBuffer, outMode, intent, flag) {
    return initWASM().then((wasm) => {
        const inputProfilePtr = wasm._malloc(inputProfileBuffer.length);
        const outputProfilePtr = wasm._malloc(outputProfileBuffer.length);

        wasm.HEAPU8.set(inputProfileBuffer, inputProfilePtr);
        wasm.HEAPU8.set(outputProfileBuffer, outputProfilePtr);
        const instance = wasm._createColorAlchemy(inputProfilePtr, inputProfileBuffer.length, inMode, outputProfilePtr, outputProfileBuffer.length, outMode, intent, flag);

        wasm._free(inputProfilePtr);
        wasm._free(outputProfilePtr);

        return new ColorAlchemy(instance, inMode, outMode);
    });
}


const FLAG_SOFTPROOFING = 0x4000;

/**
 * 创建校验 color alchemy 实例
 * @param { Uint8Array } inputProfileBuffer - 输入色彩空间的 profile
 * @param { number } mode - 颜色格式
 * @param { Uint8Array } proofingProfileBuffer - 校验色彩空间的 profile
 * @param { number } intent - 映射方式
 * @param { number } flag - 标识
 * @returns { Promise<ColorAlchemy> }
 */
export function createProofingColorAlchemy(inputProfileBuffer, mode, proofingProfileBuffer, intent, flag) {
    return initWASM().then((wasm) => {
        const inputProfilePtr = wasm._malloc(inputProfileBuffer.length);
        const proofingProfilePtr = wasm._malloc(proofingProfileBuffer.length);

        wasm.HEAPU8.set(inputProfileBuffer, inputProfilePtr);
        wasm.HEAPU8.set(proofingProfileBuffer, proofingProfilePtr);
        const instance = wasm._createColorAlchemy(inputProfilePtr, inputProfileBuffer.length, mode, proofingProfilePtr, proofingProfileBuffer.length, mode, intent, flag | FLAG_SOFTPROOFING);
        wasm._free(inputProfilePtr);
        wasm._free(proofingProfilePtr);

        return new ColorAlchemy(instance, mode, mode);
    });
}